import { wrapTextWithSpan } from '../../ts/helper';

/**
 * Friendation component
 * @param component Instance of this _Friendation_ component
 */
const BasisSuchergebnisse = async (component: HTMLElement) => {
  /** X results message container */
  const messageResults: HTMLElement =
    component.querySelector('.message--results');
  const rawMessageResults = messageResults.innerText;

  /** No results message container */
  const messageNoResults: HTMLElement = component.querySelector(
    '.message--noResults'
  );

  /** Loading message container */
  const messageLoading: HTMLElement =
    component.querySelector('.message--loading');

  /** Search form */
  const form: HTMLFormElement = component.querySelector('form');

  /** The search input that the user is typing in */
  const searchInput: HTMLInputElement = form.querySelector(
    'input[type="search"]'
  );

  /** Submit button in {@link form} */
  const submitButton = form.querySelector('button');

  /** Container with results */
  const targetContainer: HTMLElement = component.querySelector('.results-list');

  /** Controller for `fetch` in {@link fetchPreviewText} */
  let controller: AbortController;

  /** Signal for `fetch` in {@link fetchPreviewText} */
  let signal: AbortSignal;

  /**
   * Loop over all results and fetch text from the linked pages to build a preview text
   */
  const fetchPreviewText = () => {
    controller = new AbortController();

    /** Signal for `fetch` in {@link fetchPreviewText} */
    signal = controller.signal;

    const searchInput: HTMLInputElement = form.querySelector(
      'input[type="search"]'
    );

    const keyword = searchInput.value.trim();

    const results = Array.from(
      component.querySelectorAll('.results-list > li')
    );
    results.forEach(async (result) => {
      const href = result.querySelector('a')?.href;
      const textContainer: HTMLParagraphElement = result.querySelector(
        '.result-text-content'
      );

      if (href && !textContainer.innerText.trim().length) {
        const url = new URL(href);

        fetch(url.href, { signal })
          .then((x) => x.text())
          .then((x) => {
            const tempDom = document.createElement('div');
            tempDom.innerHTML = x;
            const paragraphs: HTMLParagraphElement[] = Array.from(
              tempDom.querySelectorAll('main p:not([class])')
            );
            const paragraphsWithKeyword = paragraphs.filter((x) =>
              x.innerText.includes(keyword)
            );
            let text = '[...] ';

            const paragraphLimit = 3;
            const characterLimitPerParagraph = 50;

            let paragraphsToLoop = paragraphs;

            if (paragraphsWithKeyword.length) {
              paragraphsToLoop = paragraphsWithKeyword;
            }

            paragraphsToLoop.forEach((x, i) => {
              if (i <= paragraphLimit) {
                text += ` ${x.innerText.substring(
                  0,
                  characterLimitPerParagraph
                )}...`;
              }
            });

            text = text.replaceAll('@', '[at]');

            textContainer.innerText = text;

            wrapTextWithSpan(textContainer, keyword);
          });
      }
    });
  };

  const handleAjax = () => {
    form.addEventListener('submit', (e: SubmitEvent) => {
      e.preventDefault();

      /** Cancel all fetch request initiated in {@link fetchPreviewText}. This will produce an error message in the console. */
      controller.abort('User changed search keyword');
      targetContainer.innerHTML = '';

      /** The new term that the user wants to search */
      const term = searchInput.value.trim();

      /** Current url */
      const url = new URL(window.location.href);

      /** Current serach term */
      const currentTerm = url.searchParams.get('q')?.trim();

      /** Avoid submission of the same term */
      if (currentTerm && term === currentTerm) {
        targetContainer.tabIndex = -1;
        targetContainer.focus();
        return;
      }

      /** Avoid multiple form submits */
      if (form.hasAttribute('aria-busy')) return;
      form.setAttribute('aria-busy', 'true');
      if (submitButton) submitButton.disabled = true;

      /** Hide any messages */
      messageResults.inert = true;
      messageNoResults.inert = true;

      /** Show loading message */
      messageLoading.inert = false;

      /** Add new term to the url */
      url.searchParams.set('q', term);

      /** Modify current URL in browsers address bar */
      window.history.pushState(
        {
          url: url.toString()
        },
        '',
        url.toString()
      );

      /** Fetch results for the currently used search {@link term} */
      fetch(url.href, {})
        .then((x) => x.text())
        .then((x) => {
          const tempDom = document.createElement('div');
          tempDom.innerHTML = x;

          /** Results would be here */
          const items = Array.from(
            tempDom.querySelectorAll('.results-list > li')
          );

          /** Hide loading message */
          messageLoading.inert = true;

          if (items.length) {
            /** Update the „We found X results” message */
            updateSearchResultsCount(items.length);
            /** Show the „We found X results” message */
            messageResults.inert = false;
          } else {
            /** Show the „We found nothing” message */
            messageNoResults.inert = false;
          }

          /** Clear old items */
          targetContainer.innerHTML = '';

          /** Append new items */
          items.forEach((item) => {
            /** Trigger animation */
            item.classList.add('animation--started');
            targetContainer.append(item);
          });
        })
        .then(() => {
          setTimeout(() => {
            targetContainer.tabIndex = -1;
            targetContainer.focus();

            /** Make form accessible again */
            form.removeAttribute('aria-busy');
            if (submitButton) submitButton.disabled = false;

            fetchPreviewText();
          }, 100);
        });
    });
  };

  const updateSearchResultsCount = (amount?: number) => {
    if (!amount) amount = targetContainer.childElementCount;
    messageResults.innerHTML = `<p>${rawMessageResults.replace(
      '%results%',
      amount.toString()
    )}</p>`;
  };

  window.addEventListener('beforeunload', () => {
    /** Cancel fetch requests when the user is trying to leave the page. */
    controller?.abort('User changed search keyword');
  });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      /** Cancel fetch requests when the user leaves the tab. */
      controller?.abort('User changed search keyword');
    }
  });

  updateSearchResultsCount();
  handleAjax();
  await fetchPreviewText();
};

/** Selector for any instance of this _Friendation_ component */
const selector = '[is="basis-suchergebnisse"]';

/** Function that handles a single instance of this _Friendation_ component */
const classFunction: (component: HTMLElement) => void = BasisSuchergebnisse;

/**
 * Friendation component
 */
export const aSearchResults = () => {
  /** Instances of this Friendation component */
  const components: HTMLElement[] = Array.from(
    document.querySelectorAll(selector)
  );

  components.forEach((x) => {
    classFunction.call(this, x);
  });
};
